if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        // The thumbnail row's own < > buttons only page the strip into view
        // (SliderComponent's native behavior). Make them also step which
        // photo is showing in the main viewer, like a real prev/next
        // control, by clicking the adjacent thumbnail — reuses the existing
        // setActiveMedia/setActiveThumbnail wiring above instead of
        // duplicating it.
        //
        // Real bug this surfaced: with only 4-5 thumbnails they all already
        // fit on screen, so SliderComponent's own update() immediately marks
        // both buttons `disabled` (its "nothing left to scroll to" check) —
        // and a native `disabled` button never dispatches click events at
        // all, so the buttons went permanently dead. We take ownership of
        // enabled/disabled state ourselves (based on which image is active,
        // not scroll position) and strip the native attribute the instant
        // SliderComponent reapplies it, styling the boundary state with
        // [data-nav-disabled] instead so it still looks and reads disabled.
        // Variant-only thumbnails (a swatch's own image — reachable by picking
        // that swatch, not by scrolling the gallery) are rendered into the DOM
        // but CSS-hidden via .thumbnail-list_item--variant:not(:first-child)
        // (section-main-product.css) — and are never rendered as slides in the
        // main viewer at all (product-media-gallery.liquid excludes them from
        // that loop). Stepping through `data-target` items unfiltered used to
        // land on these invisible entries: the adjacent-thumbnail .click()
        // would look up a slide that doesn't exist in the viewer, silently
        // fall back to the first slide, and desync the main image from
        // whichever thumbnail the border landed on. Only step through
        // thumbnails an actual visitor can see and click.
        this.thumbnailItems = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]')).filter(
          (item) => item.offsetParent !== null
        );
        const thumbPrevButton = this.elements.thumbnails.querySelector('button[name="previous"]');
        const thumbNextButton = this.elements.thumbnails.querySelector('button[name="next"]');

        const activeThumbIndex = () =>
          this.thumbnailItems.findIndex(
            (item) => item.querySelector('button').getAttribute('aria-current') === 'true'
          );

        const setNavButtonState = (button, isAtEnd) => {
          if (!button) return;
          button.removeAttribute('disabled');
          button.toggleAttribute('data-nav-disabled', isAtEnd);
          button.setAttribute('aria-disabled', isAtEnd);
        };

        this.syncThumbNavButtons = () => {
          const index = activeThumbIndex();
          setNavButtonState(thumbPrevButton, index <= 0);
          setNavButtonState(thumbNextButton, index === -1 || index >= this.thumbnailItems.length - 1);
        };

        const stepThumbnail = (delta) => {
          const index = activeThumbIndex();
          const nextIndex = Math.min(Math.max(index + delta, 0), this.thumbnailItems.length - 1);
          if (nextIndex === index) return;
          this.thumbnailItems[nextIndex].querySelector('button').click();
          this.syncThumbNavButtons();
        };

        if (thumbPrevButton) thumbPrevButton.addEventListener('click', () => stepThumbnail(-1));
        if (thumbNextButton) thumbNextButton.addEventListener('click', () => stepThumbnail(1));

        // SliderComponent re-disables these buttons on its own scroll/resize
        // driven update() cycle; reassert our version right after so it
        // never wins the race.
        [thumbPrevButton, thumbNextButton].forEach((button) => {
          if (!button) return;
          new MutationObserver(() => this.syncThumbNavButtons()).observe(button, {
            attributes: true,
            attributeFilter: ['disabled'],
          });
        });

        this.syncThumbNavButtons();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
        this.syncThumbNavButtons?.();
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
